import React, { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Gem, Lock, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

function passwordIssues(pw) {
  const issues = [];
  if (!pw || pw.length < 8) issues.push("At least 8 characters");
  if (!/[A-Za-z]/.test(pw || "")) issues.push("At least 1 letter");
  if (!/\d/.test(pw || "")) issues.push("At least 1 number");
  return issues;
}

// Lazy-migration screen. Reached when /api/auth/me returns
// `needs_password_setup: true`. The user must set a password before any
// other route renders (the <Protected> guard enforces this).
export default function SetPasswordPage() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const pwIssues = useMemo(() => passwordIssues(password), [password]);
  const showRules = password.length > 0 && pwIssues.length > 0;
  const mismatch = confirm.length > 0 && confirm !== password;

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    setErrorMsg("");
    if (pwIssues.length) { setErrorMsg("Please choose a stronger password."); return; }
    if (confirm !== password) { setErrorMsg("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/set-password", { password, confirm_password: confirm });
      toast.success("Password set. You’re all secured.");
      // Refresh user record so needs_password_setup flips to false, then go home.
      if (refresh) await refresh();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Could not set your password.";
      setErrorMsg(typeof msg === "string" ? msg : "Could not set your password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)] relative overflow-hidden" data-testid="set-password-page">
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <header className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-[color:var(--brand-primary)] text-[color:var(--bg-0)] flex items-center justify-center">
            <Gem className="h-4 w-4" />
          </div>
          <span className="font-display text-lg font-semibold tracking-tight">Postmoro</span>
        </Link>
      </header>
      <main className="relative max-w-[460px] mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="rounded-2xl bg-[color:var(--surface-1)] border border-[color:var(--border-subtle)] shadow-[var(--shadow-card)] p-6 sm:p-7">
          <div className="flex items-start gap-3 mb-5">
            <div className="h-9 w-9 rounded-md bg-[color:rgba(27,168,159,0.12)] text-[color:var(--brand-primary)] flex items-center justify-center shrink-0">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold tracking-tight" data-testid="set-pw-title">Set your password</h1>
              <p className="text-sm text-[color:var(--text-2)] mt-1">
                Postmoro now requires a password for {user?.email ? <span className="text-[color:var(--text-1)] font-medium">{user.email}</span> : "your account"}. Choose one to secure your account.
              </p>
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-3.5">
            <div>
              <label className="text-xs text-[color:var(--text-2)]">New password</label>
              <div className="relative mt-1.5">
                <Input
                  type={showPw ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Min 8 characters, with a letter and a number"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="set-pw-password"
                  className="pr-9 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] placeholder:text-[color:var(--text-3)]"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--text-3)] hover:text-[color:var(--text-1)]"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {showRules ? (
                <ul className="mt-2 text-[11px] text-[color:var(--text-3)] space-y-0.5">
                  {pwIssues.map((rule) => (<li key={rule}>\u2022 {rule}</li>))}
                </ul>
              ) : null}
            </div>

            <div>
              <label className="text-xs text-[color:var(--text-2)]">Confirm new password</label>
              <Input
                type={showPw ? "text" : "password"}
                autoComplete="new-password"
                placeholder="Re-enter your password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="set-pw-confirm"
                className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] placeholder:text-[color:var(--text-3)]"
                required
              />
              {mismatch ? (
                <div className="mt-1 text-[11px] text-[color:var(--danger)]">Passwords do not match.</div>
              ) : null}
            </div>

            {errorMsg ? (
              <div className="text-xs text-[color:var(--danger)] bg-[color:rgba(238,90,82,0.06)] border border-[color:rgba(238,90,82,0.30)] rounded-md px-3 py-2" data-testid="set-pw-error" role="alert">
                {errorMsg}
              </div>
            ) : null}

            <Button
              type="submit"
              disabled={loading}
              data-testid="set-pw-submit"
              className="w-full bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
            >
              {loading ? <span className="inline-flex items-center"><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</span> : <><Lock className="h-4 w-4 mr-2" />Set password</>}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
